/**
 * Sprint 24.15 — hire-from-template mutation.
 *
 * Wraps the member-grade `hire_from_template` MCP tool. The tool returns a
 * structured `equipment_missing` error when a REQUIRED connection kind isn't
 * installed (Decision 8) — we surface that as a typed `EquipmentMissingError`
 * so the customise form can show the install-or-skip modal rather than a toast.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { queryKeys } from "../../lib/query-keys";
import type { AvailabilityStatus } from "./use-agent-templates";

export interface MissingEquipment {
  kind: string;
  purpose: string | null;
  /** Sprint 24.15.2 — how this kind is installable today (drives the modal pill). */
  availability_status: AvailabilityStatus;
  availability_note: string | null;
}

export class EquipmentMissingError extends Error {
  readonly missingEquipment: MissingEquipment[];
  /** Sprint 24.15.2 — the subset of missing kinds that are installable today (block the hire). */
  readonly blockingKinds: string[];
  readonly canInstall: boolean;
  readonly installHint: string;
  readonly templateId: string;
  constructor(detail: {
    missing_equipment?: MissingEquipment[];
    blocking_kinds?: string[];
    can_install?: boolean;
    install_hint?: string;
    template_id?: string;
  }) {
    super("equipment_missing");
    this.name = "EquipmentMissingError";
    this.missingEquipment = detail.missing_equipment ?? [];
    this.blockingKinds = detail.blocking_kinds ?? [];
    this.canInstall = detail.can_install ?? false;
    this.installHint = detail.install_hint ?? "";
    this.templateId = detail.template_id ?? "";
  }
}

export interface HireFromTemplateArgs {
  template_id: string;
  name_override?: string;
  instructions_addendum?: string;
  equipment_overrides?: { kind: string; enabled: boolean }[];
  model_tier_override?: "Fast" | "Standard" | "Heavy";
  reports_to_node_id?: string;
  allow_missing_equipment?: boolean;
}

export interface HireFromTemplateResult {
  node_id: string;
  name: string;
  template_id: string;
  template_version: string;
  instantiated: {
    priorities_seeded: number;
    memories_seeded: number;
    equipped: { kind: string; connection_kind: string; connection_id: string }[];
    native_capabilities: string[];
    unmet_optional: string[];
    missing_required_skipped: string[];
    /** Sprint 24.15.2 — kinds recorded on the agent for future auto-equip. */
    deferred_equipment: string[];
  };
}

export function useHireFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: HireFromTemplateArgs): Promise<HireFromTemplateResult> => {
      const res = await mcpClient.toolsCall<HireFromTemplateResult & { error?: string }>(
        "hire_from_template",
        args as unknown as Record<string, unknown>,
      );
      if (res.isError) {
        const sc = res.structuredContent as { error?: string } | undefined;
        if (sc?.error === "equipment_missing") {
          throw new EquipmentMissingError(sc as ConstructorParameters<typeof EquipmentMissingError>[0]);
        }
        throw new Error(res.content?.[0]?.text ?? "Couldn't hire from template");
      }
      return res.structuredContent as HireFromTemplateResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.libraryTree() });
      qc.invalidateQueries({ queryKey: ["workforce-status"] });
    },
  });
}
